export default function ReviewStep({
  formData,
  branches = [],
  departments = [],
  designations = [],
  roles = [],
}) {
  const findName = (items, id) =>
    items.find((item) => item.id === id)?.name || "—";

  const maskValue = (value) => {
    if (!value) return "—";

    const lastFour = value.replace(/\s/g, "").slice(-4);
    return `•••• ${lastFour}`;
  };

  const selectedRoles = roles
    .filter((role) => formData.erpRoleIds?.includes(role.id))
    .map((role) => role.name)
    .join(", ");

  const sections = [
    {
      title: "Basic Information",
      fields: [
        { label: "Full Name", value: formData.fullName },
        { label: "Email", value: formData.email },
        { label: "Phone Number", value: formData.phone },
        { label: "Gender", value: formData.gender },
        { label: "Date of Birth", value: formData.dob },
      ],
    },
    {
      title: "Employment Details",
      fields: [
        { label: "Employee ID", value: "Generated automatically", },
        {
          label: "Department",
          value: findName(departments, formData.department),
        },
        {
          label: "Designation",
          value: findName(designations, formData.designation),
        },
        {
          label: "Branch",
          value: findName(branches, formData.branch),
        },
        { label: "Joining Date", value: formData.joiningDate },
        { label: "Employment Type", value: formData.employmentType },
        {
          label: "ERP Dashboard Access",
          value: formData.grantErpAccess ? "Enabled" : "Not enabled",
        },
        {
          label: "ERP Roles",
          value: formData.grantErpAccess
            ? selectedRoles || "No roles selected"
            : "Employee self-service only",
        },
      ],
    },
    {
      title: "Salary Details",
      fields: [
        { label: "Salary Type", value: formData.salaryType },
        ...(formData.ctc !== "" && formData.ctc != null ? [{ label: "Annual CTC", value: `₹${Number(formData.ctc).toLocaleString("en-IN")}` }] : []),
        {
          label: "Basic Salary",
          value: formData.basicSalary
            ? `₹${Number(formData.basicSalary).toLocaleString()}`
            : "",
        },
        { label: "Payment Mode", value: formData.paymentMode },
        { label: "Bank Name", value: formData.bankName },
        {
          label: "Account Number",
          value: maskValue(formData.accountNumber),
        },
        {
          label: "IFSC Code",
          value: maskValue(formData.ifscCode),
        },
      ],
    },
    {
      title: "Documents",
      fields: [
        {
          label: "Aadhaar Number",
          value: maskValue(formData.aadhaarNumber),
        },
        {
          label: "PAN Number",
          value: maskValue(formData.panNumber),
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Review Employee Information
        </h3>

        <p className="mt-2 text-sm text-gray-500">
          Please verify all information before creating the employee.
        </p>
      </div>

      <div className="bg-red-50 rounded-2xl p-6 flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-red-100 text-red-800 flex items-center justify-center text-xl font-bold">
          {formData.fullName
            ? formData.fullName
                .split(" ")
                .map((word) => word[0])
                .join("")
                .slice(0, 2)
            : "NA"}
        </div>

        <div>
          <h4 className="text-xl font-bold text-gray-900">
            {formData.fullName || "Employee Name"}
          </h4>

          <p className="text-gray-600 mt-1">
            {findName(designations, formData.designation) !== "—"
              ? findName(designations, formData.designation)
              : "Designation"}
          </p>

          <p className="text-sm text-gray-500 mt-1">
            {findName(departments, formData.department)} •{" "}
            {findName(branches, formData.branch)}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {sections.map((section) => (
          <div
            key={section.title}
            className="bg-white border border-gray-100 rounded-2xl p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-6">
              {section.title}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {section.fields.map((field) => (
                <div key={field.label}>
                  <p className="text-sm text-gray-500">{field.label}</p>

                  <p className="mt-1 font-medium text-gray-900">
                    {field.value || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-yellow-50 border border-yellow-200 p-4">
        <p className="text-sm text-yellow-800">
          Please ensure all employee information is accurate. You can go back
          to previous steps to make changes.
        </p>
      </div>
    </div>
  );
}
